/**
 * Token Manager
 * Centralized token storage and retrieval.
 * Uses sessionStorage (cleared when the tab closes).
 * TENANT-AWARE: stores keys per tenant to reduce cross-tenant mix-ups on shared machines.
 *
 * Security:
 * - Obfuscation XOR/base64 is NOT encryption (client-side reversibility).
 * - Obfuscation ≠ encryption — XSS can still exfiltrate values readable by JS.
 * - Prefer httpOnly cookies where possible; pair with CSP and input sanitization.
 */

import { getTenantStorageKey, getTenantFromHostname } from './tenant.util'
import { clearUserContextCookie } from './user-context-cookie.util'
import { clearContextTokenCookie, clearAccessTokenHandoffCookie } from './cookieToken.util'

// Note: refreshToken lives in an httpOnly cookie only.
// accessToken: httpOnly cookie (when Domain matches the page) plus in-memory copy for localhost login handshake.
// uiPermissions is derived state, stored in memory (Redux/React Query) only.
export type TokenType = 'contextToken' | 'accessToken'

/**
 * Short fingerprint for a token (non-cryptographic).
 * Used for skipBootstrapForToken so we never store the raw JWT in sessionStorage.
 */
export function getTokenFingerprint(token: string): string {
  if (!token) return ''
  let h = 0
  for (let i = 0; i < token.length; i++) {
    h = ((h << 5) - h + token.charCodeAt(i)) | 0
  }
  return `vd_skip_${Math.abs(h).toString(36)}`
}

interface TokenOptions {
  expires?: number // UNUSED - sessionStorage has no TTL. Kept for API compatibility only.
}

/**
 * Simple token obfuscation utilities
 * WARNING: This is NOT encryption! It's obfuscation for defense-in-depth.
 * Tokens are still vulnerable to XSS attacks. Use CSP and input sanitization.
 */
class TokenObfuscator {
  /** XOR key ships in the bundle — does not provide secrecy against bundle inspection. */
  private static readonly OBFUSCATION_KEY = 'VidhyantraSecure2024!@#$%';
  
  /**
   * Obfuscate a token using Base64 + XOR cipher
   */
  static obfuscate(token: string): string {
    if (!token) return token;
    
    try {
      // XOR each character with the key
      let xored = '';
      for (let i = 0; i < token.length; i++) {
        const charCode = token.charCodeAt(i) ^ this.OBFUSCATION_KEY.charCodeAt(i % this.OBFUSCATION_KEY.length);
        xored += String.fromCharCode(charCode);
      }
      
      // Base64 encode the result
      const obfuscated = btoa(xored);
      
      // Add a prefix to identify obfuscated tokens
      return `vd_${obfuscated}`;
    } catch (error) {
      // Fail fast - return null to signal corruption
      return null as any;
    }
  }
  
  /**
   * De-obfuscate a token
   */
  static deobfuscate(obfuscatedToken: string): string {
    if (!obfuscatedToken) return obfuscatedToken;
    
    try {
      // Check if token is obfuscated (has prefix)
      if (!obfuscatedToken.startsWith('vd_')) {
        // Not obfuscated, return as-is (backward compatibility)
        return obfuscatedToken;
      }
      
      // Remove prefix
      const withoutPrefix = obfuscatedToken.substring(3);
      
      // Base64 decode
      const xored = atob(withoutPrefix);
      
      // XOR each character with the key to get original
      let original = '';
      for (let i = 0; i < xored.length; i++) {
        const charCode = xored.charCodeAt(i) ^ this.OBFUSCATION_KEY.charCodeAt(i % this.OBFUSCATION_KEY.length);
        original += String.fromCharCode(charCode);
      }
      
      return original;
    } catch (error) {
      // Fail fast - return null to signal corruption
      return null as any;
    }
  }
}

/**
 * Token Manager Class
 * Provides consistent token storage and retrieval
 */
class TokenManager {
  /**
   * Login-handshake access JWT. Kept in memory only (not sessionStorage).
   * Required because httpOnly cookies with Domain=.vidhyantra.local are rejected on localhost.
   */
  private accessTokenInMemory: string | null = null

  /**
   * Get tenant-specific key for storage
   */
  private getTenantKey(key: string): string {
    return getTenantStorageKey(key, getTenantFromHostname())
  }

  /**
   * Set a token.
   * contextToken → sessionStorage (tenant-aware, obfuscated).
   * accessToken → in-memory only, for /navigation/user-context and select-context before cookies apply.
   */
  setToken(type: TokenType, token: string, _options?: TokenOptions): void {
    if (!token) {
      return
    }
    if (type === 'accessToken') {
      this.accessTokenInMemory = token
      return
    }
    if (type !== 'contextToken') {
      return
    }

    // Store in sessionStorage with tenant prefix (tenant-aware storage)
    try {
      const tenantKey = this.getTenantKey(type)
      
      // Obfuscate token before storing
      const obfuscatedToken = TokenObfuscator.obfuscate(token)
      if (!obfuscatedToken) {
        return
      }
      sessionStorage.setItem(tenantKey, obfuscatedToken)

      // Clear bootstrap/session marker so session-expiry and bootstrap logic use the new token, not a stale one
      if (type === 'contextToken') {
        sessionStorage.removeItem('skipBootstrapForToken')
      }
      
      // Remove old global key if it exists (cleanup)
      const oldGlobalKey = sessionStorage.getItem(type)
      if (oldGlobalKey) {
        sessionStorage.removeItem(type)
      }
      
      // Migration: Remove from localStorage if it exists
      try {
        localStorage.removeItem(tenantKey)
        localStorage.removeItem(type)
      } catch (migrationError) {
      }
    } catch (error) {
    }
  }

  /**
   * Get the token value as it should be sent in the Authorization header.
   * CRITICAL: Returns OBFUSCATED token (vd_ prefix).
   * Backend will handle deobfuscation - frontend should NEVER send raw JWT.
   * ONLY HANDLES: contextToken (accessToken/refreshToken are in httpOnly cookies).
   */
  getTokenForRequest(type: TokenType): string | null {
    if (type === 'accessToken') return this.accessTokenInMemory
    try {
      const tenantKey = this.getTenantKey(type)
      const stored = sessionStorage.getItem(tenantKey)
      if (stored) {
        // Return OBFUSCATED token (with vd_ prefix)
        return stored
      }
      const globalStored = sessionStorage.getItem(type)
      if (globalStored) {
        return globalStored
      }
      const fromLocal = localStorage.getItem(tenantKey) || localStorage.getItem(type)
      if (fromLocal) {
        // Token from localStorage is plain, obfuscate it
        const obfuscated = TokenObfuscator.obfuscate(fromLocal)
        if (obfuscated) {
          sessionStorage.setItem(tenantKey, obfuscated)
          try {
            localStorage.removeItem(tenantKey)
            localStorage.removeItem(type)
          } catch {}
          return obfuscated // Return obfuscated
        }
        return null // Failed to obfuscate
      }
    } catch {}
    return null
  }

  /**
   * Get a token from storage
   * Checks tenant-specific sessionStorage first, then migrates from localStorage if found
   * NOW TENANT-AWARE: Retrieves tokens from tenant-specific storage
   * OBFUSCATED: Token is de-obfuscated after retrieval
   * ONLY HANDLES: contextToken (accessToken/refreshToken are in httpOnly cookies)
   */
  getToken(type: TokenType): string | null {
    if (type === 'accessToken') return this.accessTokenInMemory
    // Try tenant-specific sessionStorage first (primary source)
    try {
      const tenantKey = this.getTenantKey(type)
      const obfuscatedToken = sessionStorage.getItem(tenantKey)
      if (obfuscatedToken) {
        // De-obfuscate token before returning
        const token = TokenObfuscator.deobfuscate(obfuscatedToken)
        // If deobfuscation failed (corruption), purge and return null
        if (!token) {
          sessionStorage.removeItem(tenantKey)
          return null
        }
        return token
      }
      
      // Check for legacy global key in sessionStorage (migrate and remove)
      const globalObfuscatedToken = sessionStorage.getItem(type)
      if (globalObfuscatedToken) {
        // De-obfuscate
        const token = TokenObfuscator.deobfuscate(globalObfuscatedToken)
        if (!token) {
          sessionStorage.removeItem(type)
          return null
        }
        // Migrate to tenant-specific key (re-obfuscate)
        const reobfuscated = TokenObfuscator.obfuscate(token)
        if (!reobfuscated) {
          return null
        }
        sessionStorage.setItem(tenantKey, reobfuscated)
        // Remove legacy global key after migration
        sessionStorage.removeItem(type)
        return token
      }
      
      // Migration from localStorage: check if token exists in localStorage
      const localStorageTenantToken = localStorage.getItem(tenantKey)
      if (localStorageTenantToken) {
        // Tokens from localStorage are plain, so obfuscate when migrating
        const obfuscated = TokenObfuscator.obfuscate(localStorageTenantToken)
        if (!obfuscated) {
          localStorage.removeItem(tenantKey)
          return null
        }
        // Migrate to sessionStorage
        sessionStorage.setItem(tenantKey, obfuscated)
        // Remove from localStorage
        localStorage.removeItem(tenantKey)
        return localStorageTenantToken
      }
      
      // Also check global localStorage key for migration
      const localStorageGlobalToken = localStorage.getItem(type)
      if (localStorageGlobalToken) {
        // Tokens from localStorage are plain, so obfuscate when migrating
        const obfuscated = TokenObfuscator.obfuscate(localStorageGlobalToken)
        if (!obfuscated) {
          localStorage.removeItem(type)
          return null
        }
        // Migrate to tenant-specific sessionStorage
        sessionStorage.setItem(tenantKey, obfuscated)
        // Remove from localStorage
        localStorage.removeItem(type)
        return localStorageGlobalToken
      }
    } catch (error) {
    }

    return null
  }

  /**
   * Remove a token from sessionStorage
   * NOW TENANT-AWARE: Removes from tenant-specific storage
   * ONLY HANDLES: contextToken (accessToken/refreshToken are in httpOnly cookies)
   */
  removeToken(type: TokenType): void {
    if (type === 'accessToken') {
      this.accessTokenInMemory = null
      return
    }
    // Remove from tenant-specific sessionStorage
    try {
      const tenantKey = this.getTenantKey(type)
      sessionStorage.removeItem(tenantKey)
      
      // Also remove global key for cleanup
      sessionStorage.removeItem(type)
      
      // Also remove from localStorage if it exists (migration cleanup)
      try {
        localStorage.removeItem(tenantKey)
        localStorage.removeItem(type)
      } catch (migrationError) {
      }
    } catch (error) {
    }
  }

  /**
   * Check if a token exists
   * ONLY HANDLES: contextToken (accessToken/refreshToken are in httpOnly cookies)
   */
  hasToken(type: TokenType): boolean {
    return this.getToken(type) !== null
  }

  /**
   * Clear all authentication tokens
   * NOTE: Only contextToken is stored. accessToken/refreshToken are in httpOnly cookies.
   */
  clearAllTokens(): void {
    this.accessTokenInMemory = null
    this.removeToken('contextToken')
  }

  /**
   * Clear all auth-related data (tokens + user data)
   * NOW TENANT-AWARE: Clears tenant-specific data
   */
  clearAllAuthData(): void {
    // Clear all tokens (tenant-aware)
    this.clearAllTokens()

    // Clear user-related data from sessionStorage (tenant-aware)
    const userDataKeys = ['user', 'context', 'currentContext', 'currentOrgSlug', 'currentBranchSlug', 'contexts']
    const tenantId = getTenantFromHostname()
    
    userDataKeys.forEach(key => {
      try {
        // Remove tenant-specific key from sessionStorage
        const tenantKey = getTenantStorageKey(key, tenantId)
        sessionStorage.removeItem(tenantKey)
        
        // Also remove global key for cleanup
        sessionStorage.removeItem(key)
        
        // Migration cleanup: remove from localStorage if exists
        try {
          localStorage.removeItem(tenantKey)
          localStorage.removeItem(key)
        } catch (migrationError) {
        }
      } catch (error) {
      }
    })
    
    // Clear cache_userContext from localStorage (used by navigation service)
    try {
      localStorage.removeItem('cache_userContext')
    } catch (error) {
    }

    // Clear user context cookie (accessible domains stored at login)
    clearUserContextCookie()
    // Clear one-time cross-subdomain token handoff cookie to prevent stale rehydration.
    clearContextTokenCookie()
    clearAccessTokenHandoffCookie()
  }

  /**
   * Clean up legacy tokens from localStorage and migrate to sessionStorage
   * Call this on app initialization to migrate from old storage
   * ONLY HANDLES: contextToken (accessToken/refreshToken were never stored client-side)
   */
  cleanupLegacyTokens(): void {
    const currentTenant = getTenantFromHostname()
    
    try {
      const tenantKey = getTenantStorageKey('contextToken', currentTenant)
      
      // Check if token already exists in sessionStorage
      const existingSessionToken = sessionStorage.getItem(tenantKey)
      
      if (!existingSessionToken) {
        // Check localStorage for tokens to migrate
        const localStorageTenantToken = localStorage.getItem(tenantKey)
        const localStorageGlobalToken = localStorage.getItem('contextToken')
        
        // Prefer tenant-specific token over global
        const tokenToMigrate = localStorageTenantToken || localStorageGlobalToken
        
        if (tokenToMigrate) {
          // Migrate to sessionStorage WITH obfuscation (defense-in-depth consistency)
          const obfuscated = TokenObfuscator.obfuscate(tokenToMigrate)
          if (obfuscated) {
            sessionStorage.setItem(tenantKey, obfuscated)
          }
        }
      }
      
      // Clean up localStorage tokens (all legacy keys)
      localStorage.removeItem(tenantKey)
      localStorage.removeItem('contextToken')
      // Also clean up old access/refresh tokens if they somehow exist
      localStorage.removeItem(getTenantStorageKey('accessToken', currentTenant))
      localStorage.removeItem(getTenantStorageKey('refreshToken', currentTenant))
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
    } catch (error) {
    }
  }

  /**
   * Get all stored tokens (for debugging)
   * NOTE: Only contextToken is stored (accessToken/refreshToken are in httpOnly cookies)
   */
  getAllTokens(): Record<TokenType, string | null> {
    return {
      contextToken: this.getToken('contextToken'),
      accessToken: this.accessTokenInMemory,
    }
  }

  /**
   * Get tokens for a specific tenant (for cross-domain migration)
   * @param tenant - Tenant identifier to get tokens for
   * @returns Object with contextToken if found, null otherwise
   * NOTE: Only contextToken is stored. accessToken/refreshToken are in httpOnly cookies.
   */
  getTokensForTenant(tenant: string): { contextToken: string } | null {
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const key = getTenantStorageKey('contextToken', tenant)
      const obfuscatedToken = sessionStorage.getItem(key)
      
      if (obfuscatedToken) {
        const token = TokenObfuscator.deobfuscate(obfuscatedToken)
        // If deobfuscation failed, purge and return null
        if (!token) {
          sessionStorage.removeItem(key)
          return null
        }
        return { contextToken: token }
      }

      return null
    } catch (error) {
      return null
    }
  }

}

// Export singleton instance
export const tokenManager = new TokenManager()

// Export convenience functions
export const setToken = (type: TokenType, token: string, options?: TokenOptions) => 
  tokenManager.setToken(type, token, options)

export const getToken = (type: TokenType): string | null => 
  tokenManager.getToken(type)

export const removeToken = (type: TokenType) => 
  tokenManager.removeToken(type)

export const hasToken = (type: TokenType): boolean => 
  tokenManager.hasToken(type)

export const clearAllTokens = () => 
  tokenManager.clearAllTokens()

export const clearAllAuthData = () => 
  tokenManager.clearAllAuthData()

export default tokenManager

